import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiUrl } from "../config/api";
import { clearClientSession, getAuthHeaders } from "../config/auth";
import {
  AppShell,
  ButtonBase,
  CardPanel,
  DataTable,
  FilterBar,
  PanelTitleRow,
  PaginationBar,
  TopbarActionGroup,
} from "../components";
import useRowSelection from "../hooks/useRowSelection";

const defaultFilters = {
  dateFrom: "",
  dateTo: "",
  studentName: "",
};

const ManageAttendance = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [uniqueStudents, setUniqueStudents] = useState([]);
  const [viewMode, setViewMode] = useState("attendance");
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const navigate = useNavigate();
  const attendanceSelection = useRowSelection(attendanceData, (row) => row.id);
  const {
    clearSelection,
    isAllSelected,
    selectedIds,
    toggleAllVisible,
    toggleSelection,
  } = attendanceSelection;

  const handleLogout = () => {
    clearClientSession();
    navigate("/");
  };

  const fetchAttendance = useCallback(
    async (activeFilters) => {
      setLoading(true);

      try {
        const queryParams = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });

        if (activeFilters.dateFrom) {
          queryParams.append("dateFrom", activeFilters.dateFrom);
        }

        if (activeFilters.dateTo) {
          queryParams.append("dateTo", activeFilters.dateTo);
        }

        if (activeFilters.studentName) {
          queryParams.append("studentName", activeFilters.studentName.trim());
        }

        const response = await fetch(
          `${apiUrl("/api/attendance/all")}?${queryParams.toString()}`,
          {
            headers: {
              ...getAuthHeaders(),
            },
          },
        );

        const result = await response.json();
        setAttendanceData(Array.isArray(result.data) ? result.data : []);
        setTotalPages(result.totalPages || 1);
      } catch (err) {
        console.error("Error fetching attendance:", err);
        setAttendanceData([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [limit, page],
  );

  const fetchUniqueStudents = useCallback(async () => {
    setLoading(true);

    try {
      const response = await fetch(apiUrl("/api/attendance/unique-students"), {
        headers: {
          ...getAuthHeaders(),
        },
      });

      const result = await response.json();
      setUniqueStudents(Array.isArray(result.data) ? result.data : []);
    } catch (err) {
      console.error("Error fetching unique students:", err);
      setUniqueStudents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === "attendance") {
      fetchAttendance(appliedFilters);
    } else {
      clearSelection();
      fetchUniqueStudents();
    }
  }, [
    appliedFilters,
    clearSelection,
    fetchAttendance,
    fetchUniqueStudents,
    viewMode,
  ]);

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const handleClear = () => {
    setPage(1);
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }

    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const exportUniqueToPDF = () => {
    const doc = new jsPDF();

    autoTable(doc, {
      head: [["Name", "Email", "Phone"]],
      body: uniqueStudents.map((student) => [
        student.name,
        student.email,
        student.phone,
      ]),
    });

    doc.save("unique_students.pdf");
  };

  const exportUniqueToExcel = () => {
    const csvContent = [
      ["Name", "Email", "Phone"],
      ...uniqueStudents.map((student) => [
        student.name,
        student.email,
        student.phone,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "unique_students.csv");
  };

  const attendanceColumns = useMemo(
    () => [
      {
        key: "select",
        label: (
          <input
            type="checkbox"
            className="ui-checkbox"
            checked={isAllSelected}
            onChange={toggleAllVisible}
          />
        ),
        mobileLabel: "Select",
        render: (entry) => (
          <input
            type="checkbox"
            className="ui-checkbox"
            checked={selectedIds.includes(entry.id)}
            onChange={() => toggleSelection(entry.id)}
          />
        ),
      },
      {
        key: "name",
        label: "Student Name",
      },
      {
        key: "email",
        label: "Student Email",
        className: "ui-data-table__muted",
      },
      {
        key: "phone",
        label: "Phone Number",
        className: "ui-data-table__muted",
      },
      {
        key: "timestamp",
        label: "Time and Date",
        className: "ui-data-table__muted",
        render: (entry) => new Date(entry.timestamp).toLocaleString(),
      },
    ],
    [isAllSelected, selectedIds, toggleAllVisible, toggleSelection],
  );

  const uniqueColumns = useMemo(
    () => [
      {
        key: "name",
        label: "Student Name",
      },
      {
        key: "email",
        label: "Student Email",
        className: "ui-data-table__muted",
      },
      {
        key: "phone",
        label: "Phone Number",
        className: "ui-data-table__muted",
      },
    ],
    [],
  );

  return (
    <AppShell
      logoPosition="bottom"
      mainClassName="ui-shell__main--admin"
      contentClassName="ui-attendance-content"
      topbarActions={
        <TopbarActionGroup
          showDashboard
          onDashboard={() => navigate("/HomePage")}
          onLogout={handleLogout}
        />
      }
    >
      <CardPanel className="ui-card-panel--wide ui-attendance-panel ui-admin-panel">
        <div className="ui-admin-card">
          <PanelTitleRow
            title={
              viewMode === "attendance"
                ? "Manage Attendance"
                : "Unique Students"
            }
          />

          {viewMode === "attendance" ? (
            <FilterBar
              fields={[
                { name: "dateFrom", type: "date", placeholder: "Select Date" },
                { name: "dateTo", type: "date", placeholder: "Select Date" },
                { name: "studentName", placeholder: "Student Name" },
              ]}
              values={filters}
              onFieldChange={handleFieldChange}
              onSubmit={handleSearch}
              onClear={handleClear}
              showClear
              submitLabel="Search"
              clearLabel="Clear"
              submitVariant="navy"
              className="ui-attendance-filter ui-admin-filter"
              extraActions={
                <ButtonBase
                  variant="danger"
                  className="ui-filter-bar__action-btn ui-attendance-unique-btn"
                  onClick={() => setViewMode("unique")}
                >
                  View Unique Students
                </ButtonBase>
              }
            />
          ) : (
            <div className="ui-admin-card__top">
              <ButtonBase
                variant="ghost"
                onClick={() => setViewMode("attendance")}
              >
                Back to Attendance Log
              </ButtonBase>
              <div className="ui-filter-bar__actions">
                <ButtonBase variant="blue" onClick={exportUniqueToPDF}>
                  Export to PDF
                </ButtonBase>
                <ButtonBase variant="green" onClick={exportUniqueToExcel}>
                  Export to Excel
                </ButtonBase>
              </div>
            </div>
          )}

          <DataTable
            columns={
              viewMode === "attendance" ? attendanceColumns : uniqueColumns
            }
            data={viewMode === "attendance" ? attendanceData : uniqueStudents}
            className="ui-attendance-table"
            keyField={(row, rowIndex) =>
              viewMode === "attendance"
                ? (row.id ?? `${row.timestamp}-${row.email}-${rowIndex}`)
                : `${row.email}-${rowIndex}`
            }
            loading={loading}
            loadingText={
              viewMode === "attendance"
                ? "Loading attendance..."
                : "Loading unique students..."
            }
            emptyText={
              viewMode === "attendance"
                ? "No attendance found."
                : "No unique students found."
            }
          />

          {viewMode === "attendance" ? (
            <PaginationBar
              page={page}
              totalPages={totalPages}
              limit={limit}
              className="ui-attendance-pagination"
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          ) : null}
        </div>
      </CardPanel>
    </AppShell>
  );
};

export default ManageAttendance;
